import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Users,
  FileText,
  UserCircle,
  Briefcase,
  Wallet,
  Diamond,
  ShoppingBag,
  Upload,
  CreditCard,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
} from "lucide-react";
import menuItems from "../../../core/constants/sidebarAdminPanelMenuItems.js";
import { useAuth } from "../../../core/provider/Auth/index.js";
import { toastify } from "../Toasts/index.js";
import { useClearCache } from "../../../core/hooks/useClearCache.js";

// ============================================
// ✅ نقشه آیکون: اسم → کامپوننت lucide
// ============================================
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  // dashboard
  dashboard_admin: LayoutDashboard,
  dashboard: LayoutDashboard,

  // users
  users: Users,
  "user-circle": UserCircle,
  userCircle: UserCircle,

  // documents
  paper: FileText,
  document: FileText,

  // business
  briefcase: Briefcase,

  // finance
  card: CreditCard,
  wallet: Wallet,
  currency_dollar: Wallet,
  dollar: Wallet,

  // diamonds
  Diamond: Diamond,
  diamond: Diamond,

  // shop
  basket: ShoppingBag,
  shop: ShoppingBag,

  // upload
  upload: Upload,

  // fallback
  default: Circle,
};

const getIcon = (iconName?: string) => {
  if (!iconName) return Circle;
  return ICON_MAP[iconName] || ICON_MAP.default;
};

// ============================================
// ✅ Sidebar
// ============================================
const SidebarAdmin = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const { logout, refreshUser } = useAuth();
  const { clearAllCache } = useClearCache();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // ===== Logout =====
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      clearAllCache();
      logout();
      toastify("success", "با موفقیت از حساب خود خارج شدید");
      localStorage.removeItem("token");
      sessionStorage.clear();
      navigate("/login", { replace: true });
      await refreshUser();
    } catch (error) {
      console.error("خطا در خروج:", error);
      toastify("error", "خطا در خروج از حساب کاربری");
    } finally {
      setIsLoggingOut(false);
    }
  };

  // ===== Toggle submenu =====
  const toggleSubmenu = (name: string) => {
    setOpenSubmenus((prev) =>
      prev.includes(name)
        ? prev.filter((n) => n !== name)
        : [...prev, name]
    );
  };

  return (
    <aside
      className={`${isOpen ? "w-[252px] min-w-[252px]" : "w-[72px] min-w-[72px]"
        } relative flex flex-col h-full bg-white border-l border-neutral-200/80 transition-all duration-300 ease-in-out shrink-0`}
    >
      {/* ===== Brand ===== */}
      <div
        className={`flex items-center h-16 border-b border-neutral-100 shrink-0 ${isOpen ? "px-4 gap-3" : "justify-center px-2"
          }`}
      >
        <div className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 shadow-sm shadow-brand-500/20 shrink-0">
          <img
            className="object w-5 h-5 brightness-0 invert"
            src="/app/assets/images/brand.webp"
            alt="brand"
          />
        </div>
        {isOpen && (
          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="text-[13px] font-extrabold tracking-tight text-neutral-800 whitespace-nowrap">
              کارت استریت
            </span>
            <span className="text-[10px] font-medium text-neutral-400 whitespace-nowrap">
              پنل مدیریت
            </span>
          </div>
        )}
      </div>

      {/* ===== Toggle Button ===== */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="absolute z-40 flex items-center justify-center w-7 h-7 transition-all duration-200 bg-white border rounded-full shadow-md -left-3.5 top-[52px] border-neutral-200 hover:border-brand-300 hover:bg-brand-50 text-neutral-500 hover:text-brand-700"
        aria-label={isOpen ? "بستن منو" : "باز کردن منو"}
      >
        <ChevronLeft
          className={`w-3.5 h-3.5 transition-transform duration-300 ${isOpen ? "rotate-0" : "rotate-180"
            }`}
        />
      </button>

      {/* ===== Navigation ===== */}
      <nav className="flex-1 min-h-0 px-3 py-3 overflow-y-auto no-scrollbar">
        {isOpen && (
          <div className="px-2 mb-2 mt-1">
            <span className="text-[10px] font-bold tracking-wider uppercase text-neutral-400">
              منوی اصلی
            </span>
          </div>
        )}

        <ul className="flex flex-col gap-0.5">
          {menuItems?.map((item: any, index: number) => {
            const hasChildren = item?.menu?.length > 0;
            const isActive = hasChildren
              ? item.menu.some(
                (sub: any) =>
                  pathname === sub.href ||
                  pathname.startsWith(sub.href + "/")
              )
              : pathname === item?.href ||
              pathname.startsWith(item?.href + "/");

            const isSubmenuOpen =
              openSubmenus.includes(item.name) || isActive;

            const IconComponent: any = getIcon(item?.icon);

            return (
              <li key={index} className="relative">
                {/* نوار فعال */}
                {isActive && (
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-brand-500 rounded-l-full transition-all duration-300" />
                )}

                <button
                  onClick={() => {
                    if (hasChildren) {
                      toggleSubmenu(item.name);
                      if (!isOpen) setIsOpen(true);
                    } else {
                      navigate(item?.href);
                    }
                  }}
                  title={!isOpen ? item?.name : undefined}
                  className={`group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all duration-200 ${isActive
                      ? "bg-brand-50/70 text-brand-700"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800"
                    } ${!isOpen
                      ? "justify-center"
                      : "justify-between"
                    }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200 ${isActive
                          ? "bg-white text-brand-700 shadow-sm shadow-brand-500/10"
                          : "bg-neutral-50 text-neutral-500 group-hover:bg-white group-hover:text-neutral-700"
                        }`}
                    >
                      <IconComponent className="w-4 h-4" />
                    </div>
                    {isOpen && (
                      <span
                        className={`text-[13px] whitespace-nowrap transition-all ${isActive
                            ? "font-bold"
                            : "font-medium"
                          }`}
                      >
                        {item?.name}
                      </span>
                    )}
                  </div>

                  {isOpen && hasChildren && (
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-300 ${isSubmenuOpen
                          ? "rotate-180"
                          : "rotate-0"
                        }`}
                    />
                  )}
                </button>

                {/* ===== Submenu ===== */}
                {hasChildren && isOpen && (
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${isSubmenuOpen
                        ? "grid-rows-[1fr] opacity-100 mt-1"
                        : "grid-rows-[0fr] opacity-0"
                      }`}
                  >
                    <div className="overflow-hidden">
                      <ul className="relative flex flex-col gap-0.5 mr-5 pr-3 my-1">
                        <span className="absolute right-0 top-1 bottom-1 w-px bg-neutral-200" />

                        {item.menu.map(
                          (subItem: any) => {
                            const isSubActive =
                              pathname ===
                              subItem.href ||
                              pathname.startsWith(
                                subItem.href +
                                "/"
                              );

                            return (
                              <li
                                key={
                                  subItem?.name
                                }
                              >
                                <button
                                  onClick={() =>
                                    navigate(
                                      subItem?.href
                                    )
                                  }
                                  className={`relative w-full flex items-center justify-between px-3 py-2 text-[12px] rounded-lg transition-all duration-200 ${isSubActive
                                      ? "text-brand-700 bg-brand-50/80 font-semibold"
                                      : "text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50 font-medium"
                                    }`}
                                >
                                  <span className="whitespace-nowrap">
                                    {
                                      subItem?.name
                                    }
                                  </span>
                                  {isSubActive && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-600 ring-2 ring-brand-100" />
                                  )}
                                </button>
                              </li>
                            );
                          }
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* ===== Footer ===== */}
      <div className="p-3 border-t border-neutral-100 shrink-0">
        {/* Settings */}
        <button
          onClick={() => navigate("/admin/settings")}
          title={!isOpen ? "تنظیمات" : undefined}
          className={`group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all duration-200 ${pathname.startsWith("/admin/settings")
              ? "bg-brand-50/70 text-brand-700"
              : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800"
            } ${!isOpen ? "justify-center" : ""}`}
        >
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200 ${pathname.startsWith("/admin/settings")
                ? "bg-white text-brand-700 shadow-sm shadow-brand-500/10"
                : "bg-neutral-50 text-neutral-500 group-hover:bg-white group-hover:text-neutral-700"
              }`}
          >
            <Settings className="w-4 h-4" />
          </div>
          {isOpen && (
            <span
              className={`text-[13px] whitespace-nowrap ${pathname.startsWith("/admin/settings")
                  ? "font-bold"
                  : "font-medium"
                }`}
            >
              تنظیمات و دسترسی‌ها
            </span>
          )}
        </button>

        {/* Logout */}
        {/* <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          title={!isOpen ? "خروج" : undefined}
          className={`group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all duration-200 text-red-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed mt-0.5 ${!isOpen ? "justify-center" : ""
            }`}
        >
          <div className="flex items-center justify-center w-8 h-8 transition bg-red-50 rounded-lg shrink-0 text-red-500 group-hover:bg-white group-hover:shadow-sm group-hover:shadow-red-500/10">
            <LogOut className="w-4 h-4" />
          </div>
          {isOpen && (
            <span className="text-[13px] font-medium whitespace-nowrap">
              {isLoggingOut
                ? "در حال خروج..."
                : "خروج از حساب"}
            </span>
          )}
        </button> */}
      </div>
    </aside>
  );
};

export default SidebarAdmin;